import {
  Component,
  forwardRef,
  OnInit,
  ChangeDetectionStrategy,
  inject,
  input,
  DestroyRef
} from '@angular/core';

import {
  ControlValueAccessor,
  FormControl,
  FormBuilder,
  FormGroup,
  NG_VALUE_ACCESSOR,
  Validators,
  NG_VALIDATORS,
  FormsModule,
  ReactiveFormsModule
} from '@angular/forms';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NameValue, ParkingSpace } from '@ui/shared/model';
import { TranslatePipe } from '@ngx-translate/core';
import { SvgIconComponent } from 'angular-svg-icon';
import { ImmomioSelect } from '@immomio/ui/select';
import { ImmomioFormFieldComponent } from '@immomio/ui/form';
import { ImmomioButtonComponent } from '@immomio/ui/button';
import { AppInputDirective } from '../form/controls/input/input.directive';
import { FormFieldLabelComponent } from '../form/form-field/form-field-label/form-field-label.component';
import { FormFieldComponent } from '../form/form-field/form-field.component';

@Component({
  selector: 'app-property-parking-space',
  templateUrl: './property-parking-space.component.html',
  styleUrls: ['./property-parking-space.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PropertyParkingSpaceComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => PropertyParkingSpaceComponent),
      multi: true
    }
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    SvgIconComponent,
    FormFieldComponent,
    FormFieldLabelComponent,
    AppInputDirective,
    ImmomioButtonComponent,
    TranslatePipe,
    ImmomioSelect,
    ImmomioFormFieldComponent
  ]
})
export class PropertyParkingSpaceComponent
  implements OnInit, ControlValueAccessor
{
  #destroyRef = inject(DestroyRef);
  private fb = inject(FormBuilder);

  public parkingSpaceTypeList = input<NameValue<string>[], NameValue<string>[]>(
    [],
    {
      transform: parkingSpaceTypeList => [
        { name: 'general.select_l', value: null },
        ...(parkingSpaceTypeList ?? [])
      ]
    }
  );

  readonly isSalesObject = input<boolean>(undefined);
  readonly includedInAdditionalCost = input(false);

  public form = this.fb.array<
    FormGroup<{
      type: FormControl<string | null>;
      price: FormControl<number>;
      count: FormControl<number>;
      purchasePrice: FormControl<number>;
    }>
  >([]);
  public value: any;

  private onChange = (value: unknown) => value;
  private onTouch = () => null;

  public ngOnInit() {
    this.form.valueChanges
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(res => this.onChange(res));
  }

  public validate() {
    return this.form.valid ? null : { missingFields: true };
  }

  public writeValue(value: ParkingSpace[]) {
    if (!value) return;

    this.getParkingSpaceFormArray.value.forEach(() =>
      this.getParkingSpaceFormArray.removeAt(0)
    );

    value.forEach(() => this.form.push(this.getParkingSpaceControl));
    this.value = value;
    this.form.patchValue(value);
  }

  public registerOnChange(fn) {
    this.onChange = fn;
  }

  public registerOnTouched(fn) {
    this.onTouch = fn;
  }

  public get getParkingSpaceFormArray() {
    return this.form;
  }

  public get getParkingSpaceControls() {
    return this.getParkingSpaceFormArray.controls as FormGroup[];
  }

  public addParkingSpace() {
    const newParkingSpace = this.getParkingSpaceControl;
    if (this.includedInAdditionalCost()) {
      newParkingSpace.controls['price'].patchValue(0);
    }
    this.getParkingSpaceFormArray.push(newParkingSpace);
  }

  public removeParkingSpace(index: number) {
    this.getParkingSpaceFormArray.removeAt(index);
  }

  private get getParkingSpaceControl() {
    return this.fb.group({
      type: [null as string | null, Validators.required],
      price: [0, Validators.compose([Validators.required, Validators.min(0)])],
      count: [1, Validators.compose([Validators.required, Validators.min(1)])],
      purchasePrice: [0, Validators.min(0)]
    });
  }
}
